import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { TFunction } from 'i18next';
import { formatAtStation, pickedDay, zoneOfLocation } from './stationTime';
import { RentalBookingDetails } from '../types/bookingCreation';
import { InvoiceConfig } from '../tenants/config';
import { formatCurrency } from './index';
import { buildLineItems } from './invoiceLineItems';
import { invoiceLegalLines } from './invoiceLegalLines';
import { PriceBreakdown, TaxBreakdownGroup } from '../api/booking/pricePreviewApiTypes';

export interface InvoicePdfOptions {
    booking: RentalBookingDetails;
    /**
     * The booking's price, from the one backend computation (#1203) —
     * `useSavedBookingPrice(orderId)`, the same breakdown the details page
     * renders. Required: the invoice has no second way to price a booking, so a
     * caller that cannot supply it must not generate one (`validateInvoiceData`
     * blocks that case) rather than fall back to re-deriving the numbers.
     */
    breakdown: PriceBreakdown;
    invoiceConfig: InvoiceConfig;
    tenantLogo: string;
    language: 'de' | 'en';
    t: TFunction;
    /** Invoice due date. Falls back to invoice date + 14 days when unset. */
    dueDate?: Date;
    /** Optional free-text note printed in the status area (e.g. a prepaid /
     * tax-office statement). Replaces the default "paid in full" line on a
     * fully paid invoice. */
    note?: string;
}

/** Localize a country value for the invoice language. Germany is the only
 * mapped value today (the validation layer is DE-only); anything else
 * passes through unchanged. */
export const localizeCountry = (
    country: string | undefined,
    language: 'de' | 'en',
): string => {
    if (!country) return '';
    const normalized = country.trim().toLowerCase();
    const isGermany = normalized === 'germany'
        || normalized === 'de'
        || normalized === 'deutschland';
    if (!isGermany) return country;
    return language === 'de' ? 'Deutschland' : 'Germany';
};

const LOGO_MAX_WIDTH = 60;
const LOGO_MAX_HEIGHT = 30;
const PAGE_LEFT = 15;
const PAGE_RIGHT = 195;
// Fixed footer band at the bottom of the page for the two-column imprint
// (company details on the left, bank details on the right).
const FOOTER_TOP_Y = 240;
// Minimum space we want between the totals block and the footer divider.
const FOOTER_HEADROOM = 10;

interface ImageResult {
    dataUrl: string;
    width: number;
    height: number;
}

const loadImageAsDataUrl = (
    src: string,
): Promise<ImageResult> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            if (img.naturalWidth === 0 || img.naturalHeight === 0) {
                reject(new Error('Image has zero dimensions'));
                return;
            }
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                reject(new Error('Canvas context not available'));
                return;
            }
            ctx.drawImage(img, 0, 0);
            resolve({
                dataUrl: canvas.toDataURL('image/png'),
                width: img.naturalWidth,
                height: img.naturalHeight,
            });
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = src;
    });
};

/**
 * The per-rate VAT rows, plus the exemption reason where a category demands
 * one (§14 (4) Nr. 8 UStG). Returns the next free Y.
 *
 * A nil row is skipped rather than printed: "VAT (0%): 0,00 €" says a tax is
 * owed and happens to be nothing, which is not what an exempt supply means —
 * the note says it instead.
 */
const drawVatRows = (
    doc: jsPDF,
    args: {
        groups: TaxBreakdownGroup[];
        exemptionNote: string | null;
        labelX: number;
        valueX: number;
        startY: number;
        language: 'de' | 'en';
        currency: string;
        t: TFunction;
    },
): number => {
    const { groups, exemptionNote, labelX, valueX, language, currency, t } = args;
    let y = args.startY;

    for (const group of groups.filter((g) => g.vatAmount !== 0)) {
        const rate = group.ratePercent.toFixed(group.ratePercent % 1 === 0 ? 0 : 2);
        doc.text(t('invoice.vat', { rate }), labelX, y);
        doc.text(
            formatCurrency(group.vatAmount, language, currency),
            valueX, y, { align: 'right' },
        );
        y += 6;
    }

    if (exemptionNote) {
        doc.text(exemptionNote, labelX, y);
        y += 6;
    }
    return y;
};

/** A Steuernummer is labelled as one, never as a USt-IdNr. (#2840 B2). */
export const generateInvoicePdf = async (
    options: InvoicePdfOptions,
): Promise<void> => {
    const { booking, breakdown, invoiceConfig, language, t } = options;
    const currency = invoiceConfig.currency ?? 'EUR';
    const doc = new jsPDF('p', 'mm', 'a4');

    // -- Logo (top-right, aspect-ratio preserved) --
    try {
        const logoResult = await loadImageAsDataUrl(options.tenantLogo);
        const aspectRatio = logoResult.width / logoResult.height;
        let logoW = LOGO_MAX_WIDTH;
        let logoH = logoW / aspectRatio;
        if (logoH > LOGO_MAX_HEIGHT) {
            logoH = LOGO_MAX_HEIGHT;
            logoW = logoH * aspectRatio;
        }
        const logoX = PAGE_RIGHT - logoW;
        doc.addImage(logoResult.dataUrl, 'PNG', logoX, 10, logoW, logoH);
    } catch {
        // Logo load failed — continue without it
    }

    // -- Customer address block (top-left; sender lives in footer) --
    let yPos = 18;
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    const customer = booking.customerDetails;
    if (customer.company) {
        doc.text(customer.company, 15, yPos);
        yPos += 5;
    }
    doc.text(
        `${customer.firstName} ${customer.lastName}`, 15, yPos,
    );
    yPos += 5;
    if (customer.address) {
        doc.text(customer.address, 15, yPos);
        yPos += 5;
    }
    const cityLine = [customer.zipCode, customer.city]
        .filter(Boolean).join(' ');
    if (cityLine) {
        doc.text(cityLine, 15, yPos);
        yPos += 5;
    }
    if (customer.country) {
        doc.text(localizeCountry(customer.country, language), 15, yPos);
    }

    // -- Invoice heading (baseline-aligned with first meta row) --
    const headingY = 78;
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(t('invoice.title'), PAGE_LEFT, headingY);

    // -- Invoice metadata (right side) --
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    // Station days (ADR-0019), as InvoiceDocumentTable renders the same
    // invoice — the browser's zone put a check-in at Berlin midnight on the
    // previous day for an operator west of Berlin.
    const dateFormat = language === 'de' ? 'dd.MM.yyyy' : 'MM/dd/yyyy';
    const fmtDate = (d: Date) => formatAtStation(d, dateFormat, language);
    const now = new Date();
    const invoiceDate = fmtDate(now);
    // Due date comes from the generation dialog — a day the operator PICKED,
    // so it is printed as that day (its label), never re-read on the station's
    // clock: that put a 25.10. picked in Helsinki on the 24th. Fall back to the
    // standard German payment term of 14 days net when none is supplied.
    const dueDate = options.dueDate
        ? formatAtStation(pickedDay(options.dueDate), dateFormat, language)
        : fmtDate(new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000));

    // Service date / period (§14 UStG — required on German invoices).
    // Single day → "Leistungsdatum", multi-day → "Leistungszeitraum".
    // The stay's days are the location's (#3219), as the backend invoice
    // service period is; the issue date above stays the tenant-wide default.
    const serviceZone = zoneOfLocation(booking.locationTimeZone);
    const fmtServiceDate = (d: Date) => formatAtStation(d, dateFormat, language, serviceZone);
    let serviceLabelKey = 'invoice.service_date';
    let serviceValue = '';
    if (booking.checkIn && booking.checkOut) {
        const ci = fmtServiceDate(new Date(booking.checkIn as unknown as string));
        const co = fmtServiceDate(new Date(booking.checkOut as unknown as string));
        if (ci === co) {
            serviceValue = ci;
        } else {
            serviceLabelKey = 'invoice.service_period';
            serviceValue = `${ci} – ${co}`;
        }
    }

    // Labels left at 120, values right-aligned at PAGE_RIGHT — so the
    // meta values, totals values, table right edge and logo right edge
    // all share the same right margin.
    const metaLabelX = 120;
    const metaValueX = PAGE_RIGHT;
    let metaY = headingY;
    const writeMetaRow = (label: string, value: string) => {
        doc.setFont('helvetica', 'bold');
        doc.text(`${label}:`, metaLabelX, metaY);
        doc.setFont('helvetica', 'normal');
        doc.text(value, metaValueX, metaY, { align: 'right' });
        metaY += 6;
    };
    writeMetaRow(
        t('invoice.invoice_number'), booking.invoiceNumber ?? '',
    );
    writeMetaRow(t('invoice.invoice_date'), invoiceDate);
    writeMetaRow(
        t('invoice.booking_number'), booking.bookingNumber ?? '',
    );
    if (serviceValue) {
        writeMetaRow(t(serviceLabelKey), serviceValue);
    }

    // -- Line items table --
    const lineItems = buildLineItems({
        booking,
        breakdown,
        invoiceConfig,
        language,
        t,
    });
    const tableHead = [[
        t('invoice.description'),
        t('invoice.quantity'),
        t('invoice.unit_price'),
        t('invoice.amount'),
    ]];
    const tableBody = lineItems.map((item) => [
        item.description,
        item.quantity,
        item.unitPrice,
        item.amount,
    ]);

    autoTable(doc, {
        startY: 108,
        head: tableHead,
        body: tableBody,
        theme: 'grid',
        headStyles: {
            fillColor: [255, 255, 255],
            textColor: [50, 50, 50],
            fontStyle: 'bold',
            fontSize: 10,
            lineColor: [180, 180, 180],
            lineWidth: 0.2,
        },
        bodyStyles: {
            fontSize: 10,
            textColor: [50, 50, 50],
            lineColor: [180, 180, 180],
            lineWidth: 0.2,
            valign: 'middle',
        },
        columnStyles: {
            // Column widths sum to 180mm so the table spans the full
            // content width (PAGE_LEFT 15 → PAGE_RIGHT 195), aligning
            // with the totals block and the footer divider.
            0: { cellWidth: 85 },
            1: { cellWidth: 25, halign: 'right' },
            2: { cellWidth: 35, halign: 'right' },
            3: { cellWidth: 35, halign: 'right' },
        },
        margin: { left: PAGE_LEFT, right: PAGE_LEFT },
        // Suppress the default text render for the description cell so
        // the didDrawCell hook below can draw the first line bold
        // (service type) and the rest regular (address).
        willDrawCell: (data) => {
            if (
                data.column.index === 0 && data.section === 'body'
                && Array.isArray(data.cell.text)
                && data.cell.text.length > 1
            ) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (data.cell as any)._stashedText = data.cell.text;
                data.cell.text = [];
            }
        },
        didDrawCell: (data) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const stashed = (data.cell as any)._stashedText as
                | string[]
                | undefined;
            if (
                data.column.index !== 0 || data.section !== 'body'
                || !stashed
            ) {
                return;
            }
            const cell = data.cell;
            const lineHeight = 4.2;
            const totalH = stashed.length * lineHeight;
            let y = cell.y + (cell.height - totalH) / 2 + 3;
            const x = cell.x + 2;
            doc.setFontSize(10);
            doc.setTextColor(50, 50, 50);
            stashed.forEach((line, i) => {
                doc.setFont(
                    'helvetica', i === 0 ? 'bold' : 'normal',
                );
                doc.text(line, x, y);
                y += lineHeight;
            });
        },
    });

    // -- Totals section --
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const finalY = (doc as any).lastAutoTable?.finalY ?? 200;
    let totalsY = finalY + 10;
    const labelX = 120;
    const valueX = PAGE_RIGHT;

    doc.setFontSize(10);

    // Every number here is the backend's (#1203, #1353). The net/tax split
    // used to be computed locally from a per-use-case rate map, which could
    // only express ONE rate per invoice — so an accommodation booking printed
    // its 19% ancillaries at 7%, and §14 (4) UStG wants the net broken out per
    // rate anyway.
    const adjustedTotal = breakdown.total;
    const groups = breakdown.taxBreakdown ?? [];

    doc.setFont('helvetica', 'normal');
    doc.text(t('invoice.subtotal'), labelX, totalsY);
    doc.text(
        formatCurrency(breakdown.net ?? adjustedTotal, language, currency),
        valueX, totalsY, { align: 'right' },
    );
    totalsY += 6;

    // One line per rate group, then the exemption reason where there is one —
    // the §14 (4) / EN 16931 BG-23 breakdown.
    totalsY = drawVatRows(doc, {
        groups,
        exemptionNote: breakdown.taxExemptionNote,
        labelX, valueX, startY: totalsY, language, currency, t,
    });

    doc.setFont('helvetica', 'bold');
    doc.text(t('invoice.total'), labelX, totalsY);
    doc.text(
        formatCurrency(adjustedTotal, language, currency),
        valueX, totalsY, { align: 'right' },
    );
    totalsY += 8;

    doc.setFont('helvetica', 'normal');
    const amountPaid = breakdown.amountPaid;
    if (amountPaid > 0) {
        doc.text(t('invoice.amount_paid'), labelX, totalsY);
        doc.text(
            formatCurrency(amountPaid, language, currency),
            valueX, totalsY, { align: 'right' },
        );
        totalsY += 6;
    }

    // Thin rule to separate Balance due as the hero number.
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.2);
    doc.line(labelX, totalsY - 3, valueX, totalsY - 3);
    totalsY += 2;

    const balanceDue = breakdown.amountDue;
    doc.setFont('helvetica', 'bold');
    doc.text(t('invoice.balance_due'), labelX, totalsY);
    doc.text(
        formatCurrency(balanceDue, language, currency),
        valueX, totalsY, { align: 'right' },
    );

    // -- Payment status + optional free-text note (body area) --
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    const statusY = totalsY + 15;
    const isPaidInFull = balanceDue <= 0;
    const note = options.note?.trim();
    // On a paid invoice a custom note replaces the default "paid in full"
    // line; on an unpaid one it's appended below the payment instruction.
    const statusBlocks: { text: string, bold: boolean }[] = [];
    if (isPaidInFull) {
        statusBlocks.push({
            text: note || t('invoice.paid_in_full'),
            bold: true,
        });
    } else {
        statusBlocks.push({
            text: t('invoice.payment_instruction', { dueDate }),
            bold: false,
        });
        if (note) statusBlocks.push({ text: note, bold: false });
    }
    let statusBottomY = statusY;
    for (const block of statusBlocks) {
        doc.setFont('helvetica', block.bold ? 'bold' : 'normal');
        const lines = doc.splitTextToSize(
            block.text,
            PAGE_RIGHT - PAGE_LEFT,
        );
        doc.text(lines, PAGE_LEFT, statusBottomY);
        statusBottomY += 4 * lines.length + 2;
    }

    // -- Footer: three-column (company | imprint | bank) --
    // Push the footer to a new page if the status text / totals would
    // collide with it. Otherwise it sits at a fixed Y at the bottom.
    let footerTop = FOOTER_TOP_Y;
    if (statusBottomY + FOOTER_HEADROOM > footerTop) {
        doc.addPage();
        footerTop = FOOTER_TOP_Y;
    }

    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.2);
    doc.line(PAGE_LEFT, footerTop, PAGE_RIGHT, footerTop);

    doc.setFontSize(8);
    doc.setTextColor(80, 80, 80);
    doc.setFont('helvetica', 'normal');

    // Asymmetric columns: the legal imprint (col 2) has the longest
    // content, so it gets the widest slot, but not by too much.
    const col1Width = 52;
    const col2Width = 72;
    const col3Width = 56;
    const col1X = PAGE_LEFT;
    const col2X = col1X + col1Width;
    const col3X = col2X + col2Width;
    const footerLineHeight = 4;

    // Helper: write possibly-wrapped text, advancing y by line count.
    const writeFooterBlock = (
        x: number,
        startY: number,
        width: number,
        lines: string[],
    ): number => {
        let y = startY;
        for (const line of lines) {
            const wrapped = doc.splitTextToSize(line, width) as string[];
            doc.text(wrapped, x, y);
            y += footerLineHeight * wrapped.length;
        }
        return y;
    };

    const colStartY = footerTop + 5;

    // Column 1: company name (possibly multi-line) + postal address.
    const nameLines = invoiceConfig.companyNameLines
        ?? [invoiceConfig.companyName];
    doc.setFont('helvetica', 'bold');
    nameLines.forEach((line, i) => {
        doc.text(line, col1X, colStartY + i * footerLineHeight);
    });
    doc.setFont('helvetica', 'normal');
    writeFooterBlock(
        col1X,
        colStartY + nameLines.length * footerLineHeight,
        col1Width - 3,
        [
            invoiceConfig.companyAddress,
            `${invoiceConfig.companyZip} ${invoiceConfig.companyCity}`,
            localizeCountry(invoiceConfig.companyCountry, language),
        ],
    );

    // Column 2: legal imprint, by the tenant's legal form (#3430):
    // `INVOICE_FOOTER_FIELDS` decides which lines, the same table as the
    // issued invoice's footer.
    const col2Lines = invoiceLegalLines(invoiceConfig, t);
    writeFooterBlock(col2X, colStartY, col2Width - 3, col2Lines);

    // Column 3: bank details.
    doc.setFont('helvetica', 'bold');
    doc.text(t('invoice.bank_details'), col3X, colStartY);
    doc.setFont('helvetica', 'normal');
    const bankLines: string[] = [];
    if (invoiceConfig.accountHolder) {
        bankLines.push(
            `${t('invoice.account_holder')}: ` +
            `${invoiceConfig.accountHolder}`,
        );
    }
    bankLines.push(
        `${t('invoice.bank_name')}: ${invoiceConfig.bankName}`,
    );
    bankLines.push(`${t('invoice.iban')}: ${invoiceConfig.iban}`);
    bankLines.push(`${t('invoice.bic')}: ${invoiceConfig.bic}`);
    writeFooterBlock(
        col3X,
        colStartY + footerLineHeight,
        col3Width - 3,
        bankLines,
    );

    // -- Save / Download --
    const invoiceNr = booking.invoiceNumber ?? booking.bookingNumber;
    const prefix = language === 'de' ? 'rechnung' : 'invoice';
    doc.save(`${prefix}-${invoiceNr}.pdf`);
};
